type ValidationErrorValue = string | string[];
type ValidationErrorsPayload = Record<string, ValidationErrorValue>;

interface ApiErrorPayload {
  Id?: unknown;
  Message?: unknown;
  Errors?: unknown;
}

export interface ParsedApiError {
  status?: number;
  id?: number;
  message?: string;
  errors: Record<string, string>;
}

interface ValidationFormRef {
  setErrors?: (errors: Record<string, string>) => void;
}

interface ApplyServerValidationOptions {
  aliases?: Record<string, string>;
  mapField?: (rawField: string) => string;
  onErrorsApplied?: (errors: Record<string, string>) => void;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) return undefined;
  return value;
}

function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value;
}

function normalizeErrorMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).join('\n');
  }
  return '';
}

function normalizeErrors(errors: unknown): Record<string, string> {
  if (!errors || typeof errors !== 'object') return {};

  const out: Record<string, string> = {};
  const payload = errors as ValidationErrorsPayload;
  Object.keys(payload).forEach((key) => {
    const message = normalizeErrorMessage(payload[key]);
    if (!message) return;
    out[key] = message;
  });
  return out;
}

function applyFieldAliases(errors: Record<string, string>, aliases?: Record<string, string>): Record<string, string> {
  if (!aliases || !Object.keys(aliases).length) return errors;

  const mapped = { ...errors };

  Object.entries(aliases).forEach(([fromField, toField]) => {
    const key = Object.keys(mapped).find((k) => k === fromField || k.toLowerCase() === fromField.toLowerCase());
    if (!key) return;

    if (!mapped[toField]) mapped[toField] = mapped[key];
    if (key !== toField) delete mapped[key];
  });

  return mapped;
}

function mapErrorFields(errors: Record<string, string>, mapField?: (rawField: string) => string): Record<string, string> {
  if (!mapField) return errors;

  const mapped: Record<string, string> = {};

  Object.entries(errors).forEach(([rawField, message]) => {
    const targetField = mapField(rawField) || rawField;
    if (!mapped[targetField]) {
      mapped[targetField] = message;
      return;
    }

    mapped[targetField] = `${mapped[targetField]}\n${message}`;
  });

  return mapped;
}

export function parseApiError(error: unknown): ParsedApiError {
  const axiosLikeError = error as any;
  const response = axiosLikeError?.response;
  const payload = response?.data as ApiErrorPayload | undefined;

  const id = asNumber(payload?.Id);
  const message = asString(payload?.Message);
  const errors = normalizeErrors(payload?.Errors);

  return {
    status: asNumber(response?.status),
    id,
    message,
    errors
  };
}

export function getServerValidationErrors(error: unknown): Record<string, string> | null {
  const parsed = parseApiError(error);
  if (parsed.status !== 422) return null;
  if (typeof parsed.id === 'number' && parsed.id !== 8) return null;
  if (!Object.keys(parsed.errors).length) return null;
  return parsed.errors;
}

export function applyServerValidationToForm(error: unknown, formRef: ValidationFormRef | null | undefined, options?: ApplyServerValidationOptions): boolean {
  const serverErrors = getServerValidationErrors(error);
  if (!serverErrors) return false;

  const mappedErrors = applyFieldAliases(mapErrorFields(serverErrors, options?.mapField), options?.aliases);
  formRef?.setErrors?.(mappedErrors);
  options?.onErrorsApplied?.(mappedErrors);
  return true;
}

export function getFirstServerValidationMessage(error: unknown): string | null {
  const serverErrors = getServerValidationErrors(error);
  if (!serverErrors) return null;
  return Object.values(serverErrors)[0] ?? null;
}
